import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Building2, MapPin, TrendingUp, Users, Globe, Handshake, Warehouse } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article3 = () => {
  const { t, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white">
      <div className="container mx-auto px-6 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <Badge className="bg-emerald-600 text-white mr-4">
              <Building2 className="h-3 w-3 mr-1" />
              Logistics Investment
            </Badge>
            <span className="text-sm text-gray-600">Trans.info / Logistics Business</span>
          </div>

          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            April 2026
            <span className="mx-2">•</span>
            <span>6 min read</span>
          </div>

          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.3.title')}
          </h1>

          <img
            src="https://cdn-trans.info/uploads/2026/04/2041fd8cfc61d0090fdcf37833b1.webp"
            alt="Prologis PLIVE European Logistics Joint Venture"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.3.intro')}
            </p>

            {/* Key Stats */}
            <div className="bg-emerald-50 p-6 rounded-lg my-8 border border-emerald-200">
              <h3 className="font-bold text-emerald-900 mb-4">
                {language === 'ko' ? 'PLIVE 핵심 수치' : 'PLIVE Key Figures'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-emerald-800">
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '시드 자산 규모' : 'Seed Assets'}
                  </h4>
                  <p className="text-2xl font-bold text-emerald-600">€1bn</p>
                  <p className="text-xs">CAD 1.6bn</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <Warehouse className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '클래스 A 물류 면적' : 'Class A Logistics Space'}
                  </h4>
                  <p className="text-2xl font-bold text-blue-600">844,000 m²</p>
                  <p className="text-xs">{language === 'ko' ? '5개국 전체' : 'Across 5 countries'}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-1 flex items-center">
                    <MapPin className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '진출 국가' : 'Countries'}
                  </h4>
                  <p className="text-2xl font-bold text-purple-600">5</p>
                  <p className="text-xs">FR · DE · NL · SE · UK</p>
                </div>
              </div>
            </div>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.3.section1.title')}
            </h2>
            <p className="mb-6">{t('article.3.p1')}</p>
            <p className="mb-6">{t('article.3.p2')}</p>
            <p className="mb-6">{t('article.3.p3')}</p>

            {/* Ownership & Governance */}
            <div className="bg-blue-50 p-6 rounded-lg my-8 border border-blue-200">
              <h3 className="font-bold text-blue-900 mb-4 flex items-center">
                <Handshake className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'PLIVE 지분 구조 및 거버넌스' : 'PLIVE Ownership Structure & Governance'}
              </h3>
              <div className="grid md:grid-cols-2 gap-6 text-sm text-blue-800">
                <div className="bg-white rounded p-4">
                  <p className="font-bold text-lg text-blue-700 mb-2">La Caisse (CDPQ)</p>
                  <p className="text-3xl font-bold text-blue-600 mb-1">70%</p>
                  <ul className="text-xs space-y-1 text-gray-600 mt-2">
                    <li>• {language === 'ko' ? '주요 주주' : 'Majority shareholder'}</li>
                    <li>• {language === 'ko' ? '장기 자본 제공' : 'Long-term capital provider'}</li>
                    <li>• {language === 'ko' ? '공동 전략 의사결정' : 'Joint strategic decision-making'}</li>
                  </ul>
                </div>
                <div className="bg-white rounded p-4">
                  <p className="font-bold text-lg text-emerald-700 mb-2">Prologis</p>
                  <p className="text-3xl font-bold text-emerald-600 mb-1">30%</p>
                  <ul className="text-xs space-y-1 text-gray-600 mt-2">
                    <li>• {language === 'ko' ? '운영 파트너' : 'Operating partner'}</li>
                    <li>• {language === 'ko' ? '자산 관리 및 개발 전문성' : 'Asset management & development expertise'}</li>
                    <li>• {language === 'ko' ? '임대 가속화 및 부동산 관리' : 'Accelerating leasing & property management'}</li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white rounded text-xs text-center text-gray-600">
                {language === 'ko'
                  ? '거래 종결 예정: 2026년 2분기 (통상적 종결 조건 및 규제 승인 조건부)'
                  : 'Expected closing: Q2 2026, subject to customary closing conditions and regulatory approvals'}
              </div>
            </div>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.3.section2.title')}
            </h2>
            <p className="mb-6">{t('article.3.p4')}</p>

            {/* Quote - La Caisse */}
            <blockquote className="border-l-4 border-emerald-500 pl-6 py-2 my-6 bg-emerald-50 rounded-r-lg">
              <p className="text-gray-700 italic text-sm leading-relaxed">{t('article.3.p5')}</p>
            </blockquote>

            {/* Countries Map */}
            <div className="bg-gray-50 p-6 rounded-lg my-8 border">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="mr-2 h-5 w-5" />
                {language === 'ko' ? 'PLIVE 출범 포트폴리오 — 5개국 물류 거점' : 'PLIVE Launch Portfolio — 5 Country Logistics Hubs'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm text-center">
                {[
                  { flag: '🇫🇷', name: language === 'ko' ? '프랑스' : 'France' },
                  { flag: '🇩🇪', name: language === 'ko' ? '독일' : 'Germany' },
                  { flag: '🇳🇱', name: language === 'ko' ? '네덜란드' : 'Netherlands' },
                  { flag: '🇸🇪', name: language === 'ko' ? '스웨덴' : 'Sweden' },
                  { flag: '🇬🇧', name: language === 'ko' ? '영국' : 'United Kingdom' },
                ].map((c) => (
                  <div key={c.name} className="bg-white rounded p-3 shadow-sm">
                    <p className="text-3xl mb-1">{c.flag}</p>
                    <p className="font-semibold text-xs">{c.name}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3 text-center">
                {language === 'ko'
                  ? '총 844,000㎡ 클래스 A 물류 공간 | 수익형 부동산 + 개발 부지 결합'
                  : '~844,000 sqm Class A logistics space | Income-generating properties + development sites'}
              </p>
            </div>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              {t('article.3.section3.title')}
            </h2>

            {/* Quote - Prologis */}
            <blockquote className="border-l-4 border-blue-500 pl-6 py-2 my-6 bg-blue-50 rounded-r-lg">
              <p className="text-gray-700 italic text-sm leading-relaxed">{t('article.3.p6')}</p>
            </blockquote>

            <p className="mb-6">{t('article.3.p7')}</p>
            <p className="mb-6">{t('article.3.p8')}</p>

            {/* Market Drivers */}
            <div className="bg-purple-50 p-6 rounded-lg my-8 border border-purple-200">
              <h3 className="font-bold text-purple-900 mb-4 flex items-center">
                <TrendingUp className="mr-2 h-5 w-5" />
                {language === 'ko' ? '유럽 물류 부동산 수요 견인 요인' : 'European Logistics Real Estate Demand Drivers'}
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm text-purple-800">
                <div className="bg-white rounded p-3">
                  <p className="font-semibold mb-1">🔄 {language === 'ko' ? '공급망 재편' : 'Supply Chain Reshaping'}</p>
                  <p className="text-xs text-gray-600">{language === 'ko' ? '기업들이 공급망을 재편하고 생산을 자국 가까이 이전' : 'Companies reshaping supply chains, moving production closer to home'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="font-semibold mb-1">🛒 {language === 'ko' ? '이커머스 성장' : 'E-Commerce Growth'}</p>
                  <p className="text-xs text-gray-600">{language === 'ko' ? '지속적인 이커머스 투자가 물류 공간 수요 증가 견인' : 'Continued e-commerce investment drives growing demand for logistics space'}</p>
                </div>
                <div className="bg-white rounded p-3">
                  <p className="font-semibold mb-1">🏗️ {language === 'ko' ? '고품질 창고 수요' : 'Quality Warehouse Demand'}</p>
                  <p className="text-xs text-gray-600">{language === 'ko' ? '주요 산업 허브 전반에 걸쳐 클래스 A 물류 시설 수요 증가' : 'Rising demand for Class A logistics facilities across key industrial hubs'}</p>
                </div>
              </div>
            </div>

            {/* Brazil Partnership Context */}
            <div className="bg-yellow-50 p-6 rounded-lg my-8 border border-yellow-200">
              <h3 className="font-bold text-yellow-900 mb-3 flex items-center">
                <Users className="mr-2 h-5 w-5" />
                {language === 'ko' ? '파트너십 배경: 브라질 합작법인 (2019)' : 'Partnership Background: Brazil JV (2019)'}
              </h3>
              <p className="text-sm text-yellow-800">
                {language === 'ko'
                  ? 'PLIVE는 양사가 2019년 브라질에서 설립한 물류 합작법인을 기반으로 합니다. 브라질 파트너십을 통해 검증된 프롤로지스의 운영 역량과 라 케스의 장기 자본이 결합되어 유럽 시장에서 더 큰 시너지를 창출할 것으로 기대됩니다.'
                  : 'PLIVE builds on the established relationship between La Caisse and Prologis, who formed a logistics joint venture in Brazil in 2019. The proven operational capabilities of Prologis and long-term capital from La Caisse are expected to create greater synergies in the European market.'}
              </p>
            </div>

            {/* Key Takeaways */}
            <div className="bg-gray-50 p-6 rounded-lg mt-8">
              <h3 className="font-bold text-gray-900 mb-4">
                {language === 'ko' ? '주요 시사점' : 'Key Takeaways'}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '프롤로지스와 라 케스(CDPQ)가 €10억(CAD 16억) 규모의 판유럽 물류 합작법인 PLIVE 출범' : 'Prologis and La Caisse (CDPQ) launch PLIVE, a €1bn (CAD 1.6bn) pan-European logistics joint venture'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '5개국(프랑스·독일·네덜란드·스웨덴·영국)에 걸쳐 84만 4,000㎡ 클래스 A 물류 공간 보유' : 'Launch portfolio covers ~844,000 sqm of Class A logistics space across France, Germany, Netherlands, Sweden and UK'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '라 케스 70%, 프롤로지스 30% 지분 구조 — 프롤로지스는 운영 파트너로 자산 관리 담당' : 'La Caisse holds 70%, Prologis 30% — Prologis serves as operating partner managing asset and development'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '2026년 2분기 거래 종결 예정 (통상적 조건 및 규제 승인 조건부)' : 'Transaction expected to close Q2 2026, subject to customary conditions and regulatory approvals'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '유럽 주요 물류 거점 전역에서 추가 인수·개발을 통한 플랫폼 확장 계획' : 'Partners plan to expand the platform through further acquisitions and development in Europe\'s main logistics corridors'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '공급망 리쇼어링, 이커머스 성장이 유럽 물류 부동산 장기 펀더멘털 뒷받침' : 'Supply chain reshoring and e-commerce growth underpin the long-term fundamentals of European logistics real estate'}</span></li>
                <li className="flex items-start"><span className="mr-2">✓</span><span>{language === 'ko' ? '2019년 브라질 합작법인으로 시작된 검증된 파트너십 기반 — 유럽으로 확장' : 'Builds on proven partnership from Brazil JV established in 2019 — now expanding to Europe'}</span></li>
              </ul>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info — €1bn logistics property venture launches in five countries.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a
              href="https://trans.info/en/logistics-property-venture-467972"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article3;
